import { Component, computed, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ProviderStatus } from '@/lib-backend';
import { RealmService } from '@/app/services/realm.service';
import { CompanyService } from '@/app/services/company.service';
import { GameTemplatesService } from '@/app/services/game-templates.service';
import { AuthService } from '@/app/services/auth.service';

@Component({
    standalone: true,
    selector: 'app-dashboard-kpis',
    imports: [RouterLink],
    template: `
        <a routerLink="/website" class="col-span-12 md:col-span-6 xl:col-span-4 card flex flex-col gap-3 h-full hover:shadow-md transition-shadow no-underline text-current">
            <div class="flex items-center gap-3">
                <div class="flex items-center justify-center bg-blue-100 dark:bg-blue-400/10 rounded shrink-0" style="width:2.5rem;height:2.5rem">
                    <i class="pi pi-globe text-blue-500 text-xl!"></i>
                </div>
                <div class="flex flex-col">
                    <span class="text-muted-color text-sm">Websites</span>
                    @if (realmService.loading() && realmService.realms().length === 0) {
                        <span class="text-2xl font-semibold">—</span>
                    } @else {
                        <span class="text-2xl font-semibold">{{ websites.total() }}</span>
                    }
                </div>
            </div>
            <div class="flex flex-col gap-1 text-sm text-muted-color flex-1 min-h-[3rem]">
                <span>+{{ websites.recent() }} last 30 days</span>
                @if (websites.providerIssues() > 0) {
                    <span class="text-red-500">{{ websites.providerIssues() }} game-server {{ websites.providerIssues() === 1 ? 'issue' : 'issues' }}</span>
                }
            </div>
            <div class="text-primary text-sm">View all →</div>
        </a>

        @if (canSeeCompanies()) {
        <a routerLink="/companies" class="col-span-12 md:col-span-6 xl:col-span-4 card flex flex-col gap-3 h-full hover:shadow-md transition-shadow no-underline text-current">
            <div class="flex items-center gap-3">
                <div class="flex items-center justify-center bg-purple-100 dark:bg-purple-400/10 rounded shrink-0" style="width:2.5rem;height:2.5rem">
                    <i class="pi pi-building text-purple-500 text-xl!"></i>
                </div>
                <div class="flex flex-col">
                    <span class="text-muted-color text-sm">Companies</span>
                    @if (companyService.loading() && companyService.companies().length === 0) {
                        <span class="text-2xl font-semibold">—</span>
                    } @else {
                        <span class="text-2xl font-semibold">{{ companies.total() }}</span>
                    }
                </div>
            </div>
            <div class="flex flex-col gap-1 text-sm text-muted-color flex-1 min-h-[3rem]">
                <span>+{{ companies.recent() }} last 30 days</span>
                <span>across {{ companies.countries() }} {{ companies.countries() === 1 ? 'country' : 'countries' }}</span>
            </div>
            <div class="text-primary text-sm">View all →</div>
        </a>
        }

        <a routerLink="/game-templates" class="col-span-12 md:col-span-6 xl:col-span-4 card flex flex-col gap-3 h-full hover:shadow-md transition-shadow no-underline text-current">
            <div class="flex items-center gap-3">
                <div class="flex items-center justify-center bg-green-100 dark:bg-green-400/10 rounded shrink-0" style="width:2.5rem;height:2.5rem">
                    <i class="pi pi-th-large text-green-500 text-xl!"></i>
                </div>
                <div class="flex flex-col">
                    <span class="text-muted-color text-sm">Game templates</span>
                    @if (templatesService.loading() && templatesService.templates().length === 0) {
                        <span class="text-2xl font-semibold">—</span>
                    } @else {
                        <span class="text-2xl font-semibold">{{ templates.total() }}</span>
                    }
                </div>
            </div>
            <div class="flex flex-col gap-1 text-sm text-muted-color flex-1 min-h-[3rem]">
                <span>{{ templates.active() }} active · {{ templates.inactive() }} inactive</span>
                @if (templates.featured() > 0) {
                    <span>{{ templates.featured() }} featured</span>
                }
            </div>
            <div class="text-primary text-sm">View all →</div>
        </a>
    `,
    host: { class: 'contents' },
})
export class KpisWidget {
    protected readonly realmService = inject(RealmService);
    protected readonly companyService = inject(CompanyService);
    protected readonly templatesService = inject(GameTemplatesService);
    private readonly authService = inject(AuthService);

    /** Companies endpoints all require global admin — hide the card otherwise. */
    protected readonly canSeeCompanies = this.authService.isGlobalAdmin;

    protected readonly websites = (() => {
        const thirtyDaysAgo = () => Date.now() - 30 * 24 * 60 * 60 * 1000;
        return {
            total: computed(() => this.realmService.realms().length),
            recent: computed(() => {
                const cutoff = thirtyDaysAgo();
                return this.realmService.realms().filter(r =>
                    new Date(r.CreatedAt).getTime() >= cutoff
                ).length;
            }),
            providerIssues: computed(() => this.realmService.realms().filter(r =>
                r.ProviderStatus === ProviderStatus.NotConfigured
                || r.ProviderStatus === ProviderStatus.Unavailable
            ).length),
        };
    })();

    protected readonly companies = (() => {
        const live = computed(() => this.companyService.companies().filter(c => c.DeletedAt === null));
        const thirtyDaysAgo = () => Date.now() - 30 * 24 * 60 * 60 * 1000;
        return {
            total: computed(() => live().length),
            recent: computed(() => {
                const cutoff = thirtyDaysAgo();
                return live().filter(c => new Date(c.CreatedAt).getTime() >= cutoff).length;
            }),
            countries: computed(() => new Set(live().map(c => c.CountryCode)).size),
        };
    })();

    protected readonly templates = {
        total: computed(() => this.templatesService.templates().length),
        active: computed(() => this.templatesService.templates().filter(t => t.Template.IsActive).length),
        inactive: computed(() => this.templatesService.templates().filter(t => !t.Template.IsActive).length),
        featured: computed(() => this.templatesService.templates().filter(t => t.Template.IsFeatured).length),
    };
}
